import React, { useState, useEffect } from 'react';
import {
  TextField, MenuItem, Button, Grid, Box, Paper,
  Divider, useMediaQuery, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, CircularProgress, IconButton, Tooltip,
  Chip, Alert,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DaumPostcode from 'react-daum-postcode';
import PersonIcon from '@mui/icons-material/Person';
import DevicesIcon from '@mui/icons-material/Devices';
import HomeIcon from '@mui/icons-material/Home';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useChannel } from '../../contexts/ChannelContext';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Autocomplete from '@mui/material/Autocomplete';
import { motion, AnimatePresence } from 'framer-motion';
import AddDeviceDialog from './AddDeviceDialog';
import PriceListDialog from './PriceListDialog';
import PickupProcessDialog from './PickupProcessDialog';

/**
 * 🎯 DeviceInfo 타입 정의
 * - 기본 기기 정보 + 시세조회 자동 추가 정보
 * - 🎧 웨어러블 가격 정보 추가 (관리자 설정 가격)
 */
interface DeviceInfo {
  deviceType: string;
  deviceModel: string;
  // 🎯 시세조회에서 자동 추가된 기기 표시용
  fromPriceQuote?: boolean;
  priceRange?: { min: number; max: number };
  deviceImage?: string;
  // 🎧 웨어러블/스마트폰/태블릿 예상 시세 (관리자 설정 가격)
  estimatedPrice?: number | null;
  // 모든 등급별 가격 정보
  allGradePrices?: { A: number; B: number; C: number; D: number; E: number } | null;
  // 사용자가 선택한 등급 (웨어러블: 구성에 해당)
  userSelectedGrade?: string;
}

const deviceTypes = ['태블릿', '웨어러블', '스마트폰'];
const BANK_LIST = [
  { code: '001', name: '카카오뱅크' },
  { code: '002', name: '농협' },
  { code: '003', name: '국민은행' },
  { code: '004', name: '신한은행' },
  { code: '005', name: '우리은행' },
  { code: '006', name: 'IBK기업' },
  { code: '007', name: '하나은행' },
  { code: '008', name: '새마을금고' },
  { code: '009', name: '우체국' },
  { code: '010', name: '케이뱅크' },
  { code: '011', name: '토스뱅크' },
  { code: '012', name: '수협' },
  { code: '013', name: 'SC제일' },
  { code: '014', name: '산업' },
  { code: '015', name: '씨티' },
  { code: '016', name: '부산' },
  { code: '017', name: '신협' },
  // ...필요시 추가
];

interface FormState {
  name: string;
  phone: string;
  devices: DeviceInfo[];
  pickupAddress: string;
  pickupAddressDetail: string;
  storeCode: string;
  bankName: string;         // 추가
  bankAccount: string;      // 추가
  bankOwner: string;    // 추가
}

interface PurchaseFormProps {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const Section = ({
  title,
  icon,
  actions,
  children,
}: {
  title: string;
  icon: React.ReactElement;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 4 }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <Box display="flex" alignItems="center">
        {React.cloneElement(icon, { sx: { color: 'primary.main', mr: 1 } })}
        <Typography variant="subtitle1" fontWeight="bold" color="primary">
          {title}
        </Typography>
      </Box>
      {actions}
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

const PurchaseForm = ({ form, setForm, onSubmit, isSubmitting }: PurchaseFormProps) => {
  const { isPersonal } = useChannel();
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [isPostcodeOpen, setIsPostcodeOpen] = useState(false);
  const [modelGuideOpen, setModelGuideOpen] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [pickupProcessDialogOpen, setPickupProcessDialogOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDeviceChange = (index: number, key: keyof DeviceInfo, value: string) => {
    setForm(prev => ({
      ...prev,
      devices: prev.devices.map((device, i) => i === index ? { ...device, [key]: value } : device),
    }));
  };

  const handleAddDevice = (device: DeviceInfo) => {
    console.log('🔍 handleAddDevice 호출됨:', device);
    setForm(prev => ({
      ...prev,
      devices: [...prev.devices, device],
    }));
  };

  const removeDevice = (index: number) => {
    setForm(prev => ({
      ...prev,
      devices: prev.devices.filter((_, i) => i !== index),
    }));
  };

  // 필수 입력값 검증
  const validate = () => {
    let newErrors: { [key: string]: string } = {};
    if (!form.bankName) newErrors.bankName = '은행명을 선택하세요.';
    if (!form.bankOwner) newErrors.accountHolder = '예금주를 입력하세요.';
    if (!form.bankAccount) newErrors.bankAccount = '계좌번호를 입력하세요.';
    form.devices.forEach((device, idx) => {
      if (!device.deviceModel) newErrors[`deviceModel${idx}`] = '모델명을 입력하세요.';
    });
    if (!form.pickupAddress) newErrors.pickupAddress = '주소를 입력하세요.';
    if (!form.pickupAddressDetail) newErrors.pickupAddressDetail = '상세 주소를 입력하세요.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    // 수거 프로세스 다이얼로그를 먼저 열기
    setPickupProcessDialogOpen(true);
  };

  const handlePickupProcessConfirm = () => {
    setPickupProcessDialogOpen(false);
    // 검증 후 실제 제출
    // if (!validate()) {
    //   window.scrollTo({ top: 0, behavior: 'smooth' });
    //   return;
    // }
    onSubmit();
  };

  const handlePickupProcessCancel = () => {
    setPickupProcessDialogOpen(false);
  };

  const openModelGuide = () => {
    setModelGuideOpen(true);
    window.history.pushState(null, '', window.location.href);
  };

  const openPriceDialog = () => {
    setPriceDialogOpen(true);
    window.history.pushState(null, '', window.location.href);
  };

  const closeModelGuide = () => {
    setModelGuideOpen(false);
    window.history.back();
  };

  const closePriceDialog = () => {
    setPriceDialogOpen(false);
    window.history.back();
  };

  useEffect(() => {
    const handlePopState = () => {
      if (modelGuideOpen) setModelGuideOpen(false);
      if (priceDialogOpen) setPriceDialogOpen(false);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [modelGuideOpen, priceDialogOpen]);

  return (
    <Paper sx={{
      p: 3, mb: 4, background: 'rgba(255,255,255,0.47)',
      backdropFilter: 'blur(18px)',
      borderRadius: 5,
      boxShadow: '0 4px 36px 0 rgba(43,64,176,0.09)',
      overflow: 'visible'
    }}>
      {/* 고객 정보 */}
      <Section title="고객 정보" icon={<PersonIcon />}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="이름" fullWidth value={form.name} variant="outlined" disabled />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="전화번호" fullWidth value={form.phone} variant="outlined" disabled />
          </Grid>
        </Grid>
      </Section>


      <Section
        title="반납 기기 정보"
        icon={<DevicesIcon />}
        actions={
          <Button
            variant="outlined"
            startIcon={<InfoOutlinedIcon />}
            onClick={openModelGuide}
            sx={{ fontSize: 14, fontWeight: 500 }}
          >
            모델명 확인방법
          </Button>
        }
      >
        {form.devices.length === 0 ? (
          // 기기가 없을 때 시각적으로 잘 보이는 추가 버튼
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 3,
              border: '2px dashed #6366f1',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#8b5cf6',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(139, 92, 246, 0.06) 100%)',
              }
            }}
          >
            <DevicesIcon sx={{ fontSize: 48, color: '#6366f1', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
              반납할 기기를 추가해주세요
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, textAlign: 'center' }}>
              태블릿, 스마트폰, 웨어러블 기기를 추가하실 수 있습니다
            </Typography>
            <Button
              variant="contained"
              onClick={() => setAddDialogOpen(true)}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: 16,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)',
                  boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
                  transform: 'translateY(-1px)',
                }
              }}
              startIcon={<AddCircleOutline />}
            >
              반납 기기 추가
            </Button>
          </Box>
        ) : (
          // 기기가 있을 때 리스트 표시
          <>
            {/* 🎯 시세조회에서 자동 추가된 기기 안내 배너 */}
            {form.devices.some(d => d.fromPriceQuote) && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SearchIcon sx={{ color: '#6366f1', fontSize: 24 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#6366f1', mb: 0.25 }}>
                      🔍 시세조회에서 선택된 모델입니다
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      주소와 계좌 정보만 입력하면 접수가 완료됩니다
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {form.devices.map((device, index) => {
              // 기기 정보 파싱 개선 (태블릿 전용)
              const deviceParts = device.deviceModel.split(' - ');
              
              // 태블릿 파싱 로직
              let modelName = '';
              let storage = '';
              let connectivity = '';
              let grade = '';
              
              if (device.deviceType === '태블릿') {
                // 삼성: "Galaxy Tab S6 Lite - 64GB - Wi-Fi"
                // 애플: "아이패드 프로 11인치 3세대 (M1) - 아이패드 프로 11\" 3세대 M1 Wi-Fi 128GB - A2377 - Wi-Fi"
                
                if (deviceParts.length >= 3) {
                  // 연결 방식은 마지막 부분에서 찾기
                  const lastPart = deviceParts[deviceParts.length - 1];
                  if (/^(Wi-Fi|Wi-Fi \+ Cellular)$/i.test(lastPart)) {
                    connectivity = lastPart;
                    // 연결 방식 제외한 나머지 부분들
                    const remainingParts = deviceParts.slice(0, -1);
                    
                    // 저장 용량 찾기 (숫자+GB/TB 패턴)
                    const storagePattern = /(\d+(?:\.\d+)?\s*(GB|TB))/i;
                    for (let i = remainingParts.length - 1; i >= 0; i--) {
                      if (storagePattern.test(remainingParts[i])) {
                        storage = remainingParts[i];
                        break;
                      }
                    }
                    
                    // 모델명은 첫 번째 부분
                    modelName = remainingParts[0] || device.deviceModel;
                  } else {
                    // 연결 방식이 없는 경우 (기존 로직)
                    modelName = deviceParts[0];
                    if (/(\d+(?:\.\d+)?\s*(GB|TB))/i.test(deviceParts[1])) {
                      storage = deviceParts[1];
                    }
                  }
                } else {
                  modelName = deviceParts[0] || device.deviceModel;
                }
                
                // 등급 정보
                if (device.userSelectedGrade) {
                  grade = String(device.userSelectedGrade) + '급';
                }
              } else {
                // 비태블릿(스마트폰/웨어러블)은 기존 로직 유지
                modelName = deviceParts.length > 1 ? deviceParts[0] : device.deviceModel;
                const secondPart = deviceParts.length > 1 ? deviceParts[1] : '';
                const storageRegex = /^(\d+GB|\d+TB|1TB)$/i;
                if (storageRegex.test(secondPart)) {
                  storage = secondPart;
                }
                if (device.userSelectedGrade) {
                  grade = String(device.userSelectedGrade) + '급';
                }
              }
              
              // 구성 정보인지 확인 (기존 로직 유지)
              const configKeywords = ['풀세트', '유닛', '본체', '기타'];
              const isConfiguration = deviceParts.length >= 2 && 
                configKeywords.some(keyword => deviceParts[1].includes(keyword));
              
              // 🎯 시세조회에서 자동 추가된 기기 여부
              const isFromPriceQuote = device.fromPriceQuote === true;

              return (
                <Paper
                  key={index}
                  component={isFromPriceQuote ? motion.div : 'div'}
                  {...(isFromPriceQuote && {
                    initial: { scale: 1 },
                    animate: { 
                      scale: [1, 1.01, 1],
                      boxShadow: [
                        '0 0 0 0 rgba(99, 102, 241, 0)',
                        '0 0 0 8px rgba(99, 102, 241, 0.15)',
                        '0 0 0 0 rgba(99, 102, 241, 0)'
                      ]
                    },
                    transition: { duration: 1.5, delay: 0.3 }
                  })}
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 2,
                    borderRadius: 3,
                    // 🎯 시세조회 기기: 강조 보더 (3px primary 컬러)
                    border: isFromPriceQuote 
                      ? '3px solid #6366f1' 
                      : '1px solid rgba(99, 102, 241, 0.1)',
                    background: isFromPriceQuote
                      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: isFromPriceQuote ? '#8b5cf6' : 'rgba(99, 102, 241, 0.2)',
                      transform: 'translateY(-1px)',
                      boxShadow: isFromPriceQuote 
                        ? '0 8px 24px rgba(99, 102, 241, 0.2)' 
                        : '0 4px 12px rgba(99, 102, 241, 0.1)',
                    }
                  }}
                >
                  {/* 🎯 시세조회 기기 배지 */}
                  {isFromPriceQuote && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderBottomLeftRadius: 8,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 12 }} />
                      시세조회
                    </Box>
                  )}

                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                        📱 반납 기기 {index + 1}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                            카테고리:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600 }}>
                            {device.deviceType}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                            모델명:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>
                            {modelName} {storage}
                          </Typography>
                          {/* 등급 CHIP */}
                          {grade && (() => {
                            const gradeOnly = grade.replace('급', '');
                            const colorMap: Record<string, { fg: string; bg: string }> = {
                              A: { fg: '#10b981', bg: 'rgba(16,185,129,0.12)' },
                              B: { fg: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
                              C: { fg: '#f59e0b', bg: 'rgba(245,158,11,0.14)' },
                              D: { fg: '#ef4444', bg: 'rgba(239,68,68,0.14)' },
                            };
                            const { fg, bg } = colorMap[gradeOnly] || colorMap.B;
                            return (
                              <Typography
                                variant="body2"
                                sx={{
                                  ml: 1,
                                  color: fg,
                                  fontWeight: 900,
                                  letterSpacing: 0.2,
                                  backgroundColor: bg,
                                  px: 1,
                                  py: 0.3,
                                  borderRadius: 1,
                                }}
                              >
                                {grade}
                              </Typography>
                            );
                          })()}
                        </Box>
                        {/* 모델 유형 (연결 방식) */}
                        {connectivity && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                              모델 유형:
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#3b82f6', fontWeight: 800, ml: 0,
                                backgroundColor: 'rgba(59, 130, 246, 0.1)', px: 1, py: 0.3, borderRadius: 1
                              }}
                            >
                              {connectivity}
                            </Typography>
                          </Box>
                        )}
                        {/* 💰 예상 시세 표시 영역 (스마트폰/태블릿/웨어러블 공통) */}
                        {(() => {
                          // 📌 예상 시세 유무 확인 (관리자 설정 가격)
                          const hasEstimatedPrice = device.estimatedPrice !== undefined && device.estimatedPrice !== null && device.estimatedPrice > 0;
                          const hasPriceRange = device.priceRange && device.priceRange.min > 0;
                          const hasAnyPriceInfo = hasEstimatedPrice || hasPriceRange;
                          
                          return (
                            <Box sx={{ mt: 1 }}>
                              {/* 🎧 예상 매입가 (단일 가격) 표시 - 스마트폰/태블릿/웨어러블 공통 */}
                              {hasEstimatedPrice && (
                                <Box 
                                  sx={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1.5,
                                    bgcolor: 'rgba(16, 185, 129, 0.1)',
                                    border: '1px solid rgba(16, 185, 129, 0.2)',
                                    mb: hasPriceRange ? 1 : 0,
                                  }}
                                >
                                  <AttachMoneyIcon sx={{ fontSize: 14, color: '#059669' }} />
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: '#059669', 
                                      fontWeight: 700,
                                      fontSize: '0.85rem',
                                    }}
                                  >
                                    예상 시세: {device.estimatedPrice!.toLocaleString()}원
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* 🎯 시세조회 기기: 예상 시세 범위 표시 */}
                              {hasPriceRange && (
                                <Box sx={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center', 
                                  gap: 0.5,
                                  px: 1.5,
                                  py: 0.5,
                                  backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                  border: '1px solid rgba(99, 102, 241, 0.2)',
                                  borderRadius: 1.5,
                                }}>
                                  <Typography variant="body2" sx={{ color: '#6366f1', fontWeight: 600 }}>
                                    💰 예상 시세:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#6366f1', fontWeight: 700 }}>
                                    {device.priceRange!.min.toLocaleString()}원 ~ {device.priceRange!.max.toLocaleString()}원
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* 🔔 예상 시세 정보 없음 → "검수후 알림 예정" Chip */}
                              {!hasAnyPriceInfo && (
                                <Chip
                                  icon={<ScheduleIcon sx={{ fontSize: 14, color: '#6b7280 !important' }} />}
                                  label="검수후 알림 예정"
                                  size="small"
                                  sx={{
                                    height: 26,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    bgcolor: 'rgba(107, 114, 128, 0.08)',
                                    color: '#6b7280',
                                    border: '1px dashed rgba(107, 114, 128, 0.3)',
                                    '& .MuiChip-icon': {
                                      ml: 0.5,
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          );
                        })()}
                        {/* 구성 정보 표시 (웨어러블 등) */}
                        {isConfiguration && deviceParts.length >= 2 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                              구성:
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#10b981', 
                                fontWeight: 700,
                                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 2,
                                fontSize: '0.875rem'
                              }}
                            >
                              {deviceParts[1]}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Tooltip title="기기 삭제">
                      <IconButton 
                        color="error" 
                        onClick={() => removeDevice(index)}
                        sx={{
                          ml: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              );
            })}
            
            {/* 추가 기기 버튼 */}
            <Box textAlign="center" mt={3}>
              <Button
                variant="outlined"
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: '#6366f1',
                  color: '#6366f1',
                  '&:hover': {
                    borderColor: '#8b5cf6',
                    backgroundColor: 'rgba(99, 102, 241, 0.04)',
                  }
                }}
                startIcon={<AddCircleOutline />}
              >
                기기 추가
              </Button>
            </Box>
          </>
        )}
        
        <AddDeviceDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onAdd={handleAddDevice}
        />
      </Section>

      {/* 수거 주소 */}
      <Section title="수거 주소" icon={<HomeIcon />}>
        <Grid container spacing={2}>
          {isMobile ? (
            <>
              <Grid item xs={12}>
                <Button variant="outlined" fullWidth onClick={() => setIsPostcodeOpen(true)}>주소 검색</Button>
              </Grid>
              <Grid item xs={12}>
                <TextField label="'주소 검색' 버튼 클릭해주세요." fullWidth value={form.pickupAddress} InputProps={{ readOnly: true }} variant="outlined" error={!!errors.pickupAddress} helperText={errors.pickupAddress} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="상세 주소" fullWidth value={form.pickupAddressDetail} onChange={(e) => setForm(prev => ({ ...prev, pickupAddressDetail: e.target.value }))} variant="outlined" autoComplete="off" placeholder="상세주소를 입력하세요" error={!!errors.pickupAddressDetail} helperText={errors.pickupAddressDetail} />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={9}>
                <TextField label="주소를 입력하려면 '주소 검색' 버튼을 클릭하세요." fullWidth value={form.pickupAddress} InputProps={{ readOnly: true }} variant="outlined" error={!!errors.pickupAddress} helperText={errors.pickupAddress} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button variant="outlined" fullWidth sx={{ height: '100%' }} onClick={() => setIsPostcodeOpen(true)}>주소 검색</Button>
              </Grid>
              <Grid item xs={12}>
                <TextField label="상세 주소" fullWidth value={form.pickupAddressDetail} onChange={(e) => setForm(prev => ({ ...prev, pickupAddressDetail: e.target.value }))} variant="outlined" autoComplete="off" placeholder="상세주소를 입력하세요" error={!!errors.pickupAddressDetail} helperText={errors.pickupAddressDetail} />
              </Grid>
            </>
          )}
        </Grid>


      </Section>

      {/* 매장 코드 - B2C(개인고객) 채널에서는 숨김 처리 */}
      {!isPersonal && (
        <Section title="매장 코드" icon={<StorefrontIcon />}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="매장 코드"
                fullWidth
                value={form.storeCode}
                placeholder="매장 코드를 입력하세요"
                onChange={(e) => setForm(prev => ({ ...prev, storeCode: e.target.value }))}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Section>
      )}

      {/* 입금 계좌 정보 */}
      <Section title="입금 계좌정보" icon={<AccountBalanceIcon />}>
        <Grid container spacing={2}>
          {/* 1. 예금주 (이름) - 수정 불가 */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="예금주"
              fullWidth
              value={form.name}
              disabled
              InputProps={{
                style: { background: "#f7f7f7" }, // 비활성화 배경
              }}
            />
          </Grid>

          {/* 2. 은행명 (Autocomplete) */}
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={BANK_LIST}
              getOptionLabel={(option) => option.name}
              value={BANK_LIST.find(b => b.name === form.bankName) || null}
              onChange={(_, newValue) =>
                setForm(prev => ({ ...prev, bankName: newValue ? newValue.name : '' }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="은행명"
                  required
                  error={!!errors.bankName}
                  helperText={errors.bankName}
                />
              )}
              isOptionEqualToValue={(option, value) => option.name === value.name}
              clearOnEscape
            />
          </Grid>

          {/* 3. 계좌번호 */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="계좌번호"
              fullWidth
              value={form.bankAccount}
              onChange={(e) => setForm(prev => ({ ...prev, bankAccount: e.target.value }))}
              required
              error={!!errors.bankAccount}
              helperText={errors.bankAccount}
              autoComplete="off"
            />
          </Grid>
        </Grid>
        <Typography mt={1} color="textSecondary" fontSize={14}>
          * 반드시 본인 명의의 계좌정보를 입력해주세요.
        </Typography>
      </Section>

      {/* 단가표 다이얼로그 */}
      <PriceListDialog 
        open={priceDialogOpen} 
        onClose={closePriceDialog} 
      />

      {/* 주소 검색 Dialog */}
      <Dialog open={isPostcodeOpen} onClose={() => setIsPostcodeOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>주소 검색</DialogTitle>
        <DialogContent>
          {isPostcodeOpen && (
            <Box sx={{ height: 400 }}>
              <DaumPostcode onComplete={(data) => {
                setForm(prev => ({ ...prev, pickupAddress: data.address }));
                setIsPostcodeOpen(false);
              }} style={{ width: '100%', height: '100%' }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPostcodeOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 모델명 가이드 다이얼로그 */}
      <Dialog 
        open={modelGuideOpen} 
        onClose={closeModelGuide} 
        fullWidth 
        maxWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxWidth: isMobile ? '100vw' : '95vw',
            maxHeight: isMobile ? '100vh' : '95vh',
            margin: isMobile ? 0 : 2,
          }
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          {/* 닫기 버튼 오버레이 */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              borderRadius: '50%',
            }}
          >
            <IconButton 
              onClick={closeModelGuide}
              sx={{ 
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          {/* 이미지 컨테이너 */}
          <DialogContent 
            sx={{ 
              p: 0, 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              height: '100%',
              overflow: 'hidden'
            }}
          >
            <img
              src="/assets/model-guide.png"
              alt="모델명 확인 이미지"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                maxWidth: 'none',
                maxHeight: 'none',
              }}
              onClick={(e) => {
                // 이미지 클릭 시 확대/축소 토글
                const img = e.target as HTMLImageElement;
                if (img.style.objectFit === 'contain') {
                  img.style.objectFit = 'cover';
                  img.style.cursor = 'zoom-out';
                } else {
                  img.style.objectFit = 'contain';
                  img.style.cursor = 'zoom-in';
                }
              }}
              onLoad={(e) => {
                (e.target as HTMLImageElement).style.cursor = 'zoom-in';
              }}
            />
          </DialogContent>
        </Box>
      </Dialog>

      {/* 제출 버튼 */}
      <Box mt={3} textAlign="right">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            fontWeight: 700,
            fontSize: 17,
            borderRadius: 99,
            px: 5,
            py: 1.7,
            background: 'linear-gradient(90deg, #0ea5e9 0%, #8b5cf6 100%)',
            boxShadow: '0 2px 12px 0 rgba(139,92,246,0.13)',
            textTransform: 'none',
            transition: 'all 0.18s cubic-bezier(.4,2,.6,1)',
            '&:hover': {
              filter: 'brightness(1.10) saturate(1.1)',
              background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
              boxShadow: '0 5px 17px 0 rgba(139,92,246,0.15)'
            }
          }}
        >
          {isSubmitting ? '신청 중...' : '매입신청하기'}
        </Button>
      </Box>

      {/* 수거 프로세스 안내 다이얼로그 */}
      <PickupProcessDialog
        open={pickupProcessDialogOpen}
        onClose={handlePickupProcessCancel}
        onConfirm={handlePickupProcessConfirm}
        formData={form}
      />
    </Paper>
  );
};

export default PurchaseForm;
